/**
 * Create Put-Away Module ("Opprett utlegg")
 *
 * Manual trigger for the put-away rule group — the Compass counterpart of the legacy
 * ribbon button "Opprett utlegg". Runs the rule group configured in
 * SystemSetup.StdPutAwayRuleGroupID over ALL open grunnlag (StcIntRelocation,
 * TransType=1, LineStatus=20 — receiving registrations waiting for put-away) and
 * creates placement orders. Selective creation for chosen lines lives in the
 * Grunnlag screen; this tool takes everything that is waiting.
 */

import React, { useState } from 'react';
import { ArrowUpFromLine, Loader2, CheckCircle2, AlertTriangle, Info, PackageCheck, ChevronDown, ChevronUp } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { putAwayService, RunPutAwayResponse } from '../../../../services/putAwayService';

interface CreatePutAwayAppProps {
  onBack?: () => void;
}

export const CreatePutAwayApp: React.FC<CreatePutAwayAppProps> = ({ onBack }) => {
  const { t } = useTranslation();

  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<RunPutAwayResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showHow, setShowHow] = useState(false);

  const runPutAway = async () => {
    setIsRunning(true);
    setError(null);
    setResult(null);

    try {
      const response = await putAwayService.runPutAway();
      setResult(response);
      if (!response.success) {
        setError(response.errorMessage || t('manager.modules.createPutAway.unknownError', {}, 'Ukjent feil'));
      }
    } catch (err: any) {
      setError(err.message || t('manager.modules.createPutAway.unknownError', {}, 'Ukjent feil'));
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <BackButton onClick={onBack || (() => {})} />
          <ArrowUpFromLine className="w-8 h-8 text-orange-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {t('manager.modules.createPutAway.title', {}, 'Opprett utlegg')}
            </h1>
            <p className="text-sm text-gray-500">
              {t('manager.modules.createPutAway.subtitle', {}, 'Opprett plasseringsordre for varemottak som venter på utlegg')}
            </p>
          </div>
        </div>

        {/* Explanation */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-600 space-y-2">
              <p>
                {t(
                  'manager.modules.createPutAway.description1',
                  {},
                  'Funksjonen finner alle mottaksregistreringer (grunnlag) som venter på utlegg, velger lagerlokasjon for hver vare og oppretter plasseringsordre.'
                )}
              </p>
              <p>
                {t(
                  'manager.modules.createPutAway.description2',
                  {},
                  'Ordrene utføres av operatørene i Utlegg-modulen. Grunnlag som allerede er tildelt en ordre blir ikke tatt med på nytt. Vil du opprette utlegg for et utvalg linjer, bruk Grunnlag-skjermen.'
                )}
              </p>

              {/* Expandable step-by-step explanation */}
              <button
                onClick={() => setShowHow(!showHow)}
                className="flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium mt-1"
              >
                {showHow ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                {t('manager.modules.createPutAway.how.title', {}, 'Hva skjer når du trykker?')}
              </button>

              {showHow && (
                <div className="mt-2 space-y-2 border-l-2 border-blue-200 pl-4">
                  <ol className="list-decimal list-inside space-y-2">
                    <li>
                      <span className="font-medium text-gray-700">
                        {t('manager.modules.createPutAway.how.step1Title', {}, 'Finn grunnlag.')}
                      </span>{' '}
                      {t(
                        'manager.modules.createPutAway.how.step1',
                        {},
                        'Alle mottaksregistreringer som venter på utlegg hentes. Grunnlag som allerede er tildelt en åpen plasseringsordre hoppes over.'
                      )}
                    </li>
                    <li>
                      <span className="font-medium text-gray-700">
                        {t('manager.modules.createPutAway.how.step2Title', {}, 'Velg lokasjon.')}
                      </span>{' '}
                      {t(
                        'manager.modules.createPutAway.how.step2',
                        {},
                        'For hver vare velges utleggslokasjon: standardlokasjon fra ERP der det er satt, ellers algoritmisk valg ut fra lokasjonstype, kapasitet og sone. Én mottakslinje kan splittes over flere lokasjoner.'
                      )}
                    </li>
                    <li>
                      <span className="font-medium text-gray-700">
                        {t('manager.modules.createPutAway.how.step3Title', {}, 'Reservér og opprett ordre.')}
                      </span>{' '}
                      {t(
                        'manager.modules.createPutAway.how.step3',
                        {},
                        'Varene reserveres på mållokasjonene og plasseringsordre opprettes — regeloppsettet kan gruppere linjene i flere ordrer (f.eks. per sone). Alt skjer i én transaksjon.'
                      )}
                    </li>
                    <li>
                      <span className="font-medium text-gray-700">
                        {t('manager.modules.createPutAway.how.step4Title', {}, 'Grunnlaget markeres.')}
                      </span>{' '}
                      {t(
                        'manager.modules.createPutAway.how.step4',
                        {},
                        'Grunnlagsrader som fikk ordrelinjer settes til «Tildelt ordre». Rader uten mulig lokasjon blir stående og prøves igjen ved neste kjøring.'
                      )}
                    </li>
                    <li>
                      <span className="font-medium text-gray-700">
                        {t('manager.modules.createPutAway.how.step5Title', {}, 'Svar.')}
                      </span>{' '}
                      {t(
                        'manager.modules.createPutAway.how.step5',
                        {},
                        'Skjermen viser ordrenummer, antall grunnlag som ble tildelt og antall linjer. Venter ingenting på utlegg, opprettes ingenting.'
                      )}
                    </li>
                  </ol>
                  <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-2">
                    {t(
                      'manager.modules.createPutAway.how.important',
                      {},
                      'Viktig: Knappen flytter ingen varer — den lager bare arbeidsordrene. Selve utlegget gjør operatøren i Utlegg-modulen, og først da oppdateres lagersaldoen.'
                    )}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Run button */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6 flex flex-col items-center gap-4">
          <button
            onClick={runPutAway}
            disabled={isRunning}
            className="flex items-center gap-2 px-6 py-3 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors"
          >
            {isRunning ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                {t('manager.modules.createPutAway.running', {}, 'Oppretter utlegg...')}
              </>
            ) : (
              <>
                <ArrowUpFromLine className="w-5 h-5" />
                {t('manager.modules.createPutAway.runButton', {}, 'Opprett utlegg')}
              </>
            )}
          </button>
          <p className="text-xs text-gray-400">
            {t(
              'manager.modules.createPutAway.runHint',
              {},
              'Kjøringen kan trygt gjentas — den oppretter kun det som mangler.'
            )}
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-800">
                {t('manager.modules.createPutAway.errorTitle', {}, 'Kunne ikke opprette utlegg')}
              </p>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Result */}
        {result && result.success && (
          <div
            className={`rounded-lg border p-4 sm:p-6 ${
              result.putAwayNeeded
                ? 'bg-green-50 border-green-200'
                : 'bg-blue-50 border-blue-200'
            }`}
          >
            {result.putAwayNeeded ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                  <h2 className="text-lg font-semibold text-green-800">
                    {result.stcOrderNos.length > 1
                      ? t('manager.modules.createPutAway.successTitleMultiple', { '0': String(result.stcOrderNos.length) }, `${result.stcOrderNos.length} plasseringsordre opprettet`)
                      : t('manager.modules.createPutAway.successTitle', {}, 'Plasseringsordre opprettet')}
                  </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                  <div className="bg-white rounded-md border border-green-200 p-3">
                    <p className="text-gray-500">
                      {result.stcOrderNos.length > 1
                        ? t('manager.modules.createPutAway.orderNos', {}, 'Ordrenumre')
                        : t('manager.modules.createPutAway.orderNo', {}, 'Ordrenummer')}
                    </p>
                    <p className="text-xl font-bold text-gray-800">
                      {result.stcOrderNos.map((no) => `#${no}`).join(', ')}
                    </p>
                  </div>
                  <div className="bg-white rounded-md border border-green-200 p-3">
                    <p className="text-gray-500">
                      {t('manager.modules.createPutAway.processedCount', {}, 'Grunnlag tildelt')}
                    </p>
                    <p className="text-xl font-bold text-gray-800">{result.processedCount}</p>
                  </div>
                  <div className="bg-white rounded-md border border-green-200 p-3">
                    <p className="text-gray-500">
                      {t('manager.modules.createPutAway.lineCount', {}, 'Ordrelinjer')}
                    </p>
                    <p className="text-xl font-bold text-gray-800">{result.totalLineCount}</p>
                  </div>
                </div>
                {result.message && (
                  <p className="text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-md p-3">
                    {result.message}
                  </p>
                )}
                <p className="text-sm text-green-700 flex items-center gap-2">
                  <PackageCheck className="w-4 h-4" />
                  {t(
                    'manager.modules.createPutAway.nextStep',
                    {},
                    'Ordrene er klare for utførelse i Utlegg-modulen.'
                  )}
                </p>
              </div>
            ) : (
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0" />
                <div>
                  <h2 className="text-lg font-semibold text-blue-800">
                    {t('manager.modules.createPutAway.noNeedTitle', {}, 'Ingenting venter på utlegg')}
                  </h2>
                  <p className="text-sm text-blue-700 mt-1">
                    {result.message ||
                      t(
                        'manager.modules.createPutAway.noNeedText',
                        {},
                        'Alle mottaksregistreringer er allerede tildelt plasseringsordre.'
                      )}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
